import React, { useEffect, useState } from 'react';
import './settings.scss';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import CameraForm from './CameraForm';
import { getSystemData } from '../../System/reducer';
import {
  getResponseError, responseLogWithNotification, toDMS, useUpdatePeriod,
} from '../../../utils/utils';
import { deleteCamera, loadCameras } from '../actions';
import { getCamerasData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import { showGlobalConfirm } from '../../System/actions';
import notificationDecorator, {
  errorNotification,
  successNotification,
} from '../../Notifications/notificationDecorator';
import CameraView from '../../Dashboard/components/CameraView';
import EditCameraPTZ from '../../Dashboard/components/EditCameraPTZ';

export default function CameraSettingsColumns() {
  const [selectedId, setSelectedId] = useState(null);
  const [isShowForm, setIsShowForm] = useState(false);
  const [showPTZ, setShowPtz] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;
  const loadData = () => {
    const p1 = dispatch(loadCameras()).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);

  const create = () => {
    setSelectedId(null);
    setIsShowForm(true);
  };
  const cancel = () => {
    setSelectedId(null);
    setIsShowForm(false);
  };
  const handleEdit = item => event => {
    setSelectedId(item.id);
    setIsShowForm(true);
    event.preventDefault();
    event.stopPropagation();
    return false;
  };
  const handleDelete = item => event => {
    dispatch(showGlobalConfirm({
      confirmMessage: formatMessage({ id: 'camera.delete.message' }, { name: item.name || '' }),
      confirmButtonText: formatMessage({ id: 'app.delete' }),
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteCamera(item.id)).then(() => {
        loadData();
        successNotification(formatMessage({ id: 'camera.delete.title' }), formatMessage({ id: 'camera.delete.success' }));
      }).catch(response => {
        errorNotification(formatMessage({ id: 'camera.delete.title' }), getResponseError(response));
      });
    }).catch(() => {});

    event.preventDefault();
    event.stopPropagation();
    return false;
  };
  const renderCoordinate = () => {
    if (!selectedCamera || !selectedCamera.longitude || !selectedCamera.latitude) {
      return null;
    }
    const dms = toDMS(selectedCamera.longitude, selectedCamera.latitude);
    return (
      <>
        {formatMessage({ id: 'longitude' })} — {selectedCamera.longitude || ''} ° ({dms.longitude.deg}° {dms.longitude.min}’ {dms.longitude.sec}”)<br />
        {formatMessage({ id: 'latitude' })} — {selectedCamera.latitude || ''} ° ({dms.latitude.deg}° {dms.latitude.min}’ {dms.latitude.sec}”)
      </>
    );
  };

  const {
    cameraList = [], loadingCameraList, loadingCreateCamera, loadingSaveCamera, loadingDeleteCamera,
  } = useSelector(getCamerasData);
  const selectedCamera = (cameraList || []).find(item => item.id === selectedId);
  return (
    <>
      <Loader loading={loadingCameraList || loadingCreateCamera || loadingSaveCamera || loadingDeleteCamera} />
      <div className="settings-category frame">
        <div className="settings-header">
          <div className="settings-label">{formatMessage({ id: 'cameras' })}</div>
          <div className="settings-buttons">
            <Button variant="secondary" onClick={create}><Icons customIcon icon="plus" /> {formatMessage({ id: 'app.create' })}</Button>
          </div>
        </div>
        <div className="settings-list-wrapper">
          <div className="settings-list custom-scroll-theme">
            {cameraList.map((item, key) => (
              <div
                key={key}
                onClick={() => {
                  setSelectedId(item.id);
                  setShowPtz(false);
                }}
                className={classNames('settings-menu-item', { selected: selectedId === item.id })}
              >
                {item.name}
                <div className="edit-and-delete-buttons">
                  <div className="menu-item-button" onClick={handleEdit(item)}><Icons
                    customIcon
                    icon="pencil"
                    width={16}
                    height={16}
                  />
                  </div>
                  <div className="menu-item-button" onClick={handleDelete(item)}><Icons
                    customIcon
                    icon="trash"
                    width={16}
                    height={16}
                  />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="settings-options frame">
        {!selectedCamera && !isShowForm ? (
          <div className="settings-no-camera">
            <div className="settings-no-camera-text">{formatMessage({ id: 'camera.select' })}</div>
          </div>
        ) : null}
        {isShowForm
          ? (
            <CameraForm
              key={selectedId + isShowForm}
              selectedCamera={selectedCamera}
              selectCamera={setSelectedId}
              cancel={cancel}
              loadData={loadData}
            />
          ) : null}
        {selectedCamera && !isShowForm
          ? (
            <div className="settings-edit">
              <div className="settings-header">
                <div className="settings-label">{formatMessage({ id: 'menu.settings_camera' })}</div>
              </div>
              <div className="settings-form camera-edit-form">
                <div className="camera-edit-main">
                  <div className="camera-options settings-view-form">
                    <div className="settings-view-row">
                      <div className="settings-view-label">{formatMessage({ id: 'camera.name' })}:</div>
                      <div className="settings-view-value">{selectedCamera.name}</div>
                    </div>
                    <div className="settings-view-row">
                      <div className="settings-view-label">{formatMessage({ id: 'ip' })}:</div>
                      <div className="settings-view-value">{selectedCamera.ip}</div>
                    </div>
                    <div className="settings-view-row">
                      <div className="settings-view-label">{formatMessage({ id: 'port' })}:</div>
                      <div className="settings-view-value">{selectedCamera.port}</div>
                    </div>
                    <div className="settings-view-row">
                      <div className="settings-view-label">{formatMessage({ id: 'login' })}:</div>
                      <div className="settings-view-value">{selectedCamera.login}</div>
                    </div>
                    <div className="settings-view-row">
                      <div className="settings-view-label">{formatMessage({ id: 'camera.gps' })}:</div>
                    </div>
                    <div className="settings-view-value">
                      {renderCoordinate()}
                    </div>
                  </div>
                  <div className="camera">
                    <div className="camera-view">
                      <div className={classNames('cameras-block', { 'show-ptz': showPTZ })}>
                        <CameraView
                          selectedCamera={selectedCamera}
                          noCameraText={formatMessage({ id: 'camera.select.add' })}
                        />
                        <div className="camera-control control-fullscreen" onClick={() => setShowPtz(!showPTZ)}>
                          <Icons customIcon icon={showPTZ ? 'smallscreen' : 'fullscreen'} className="icon" />
                        </div>
                        {showPTZ && selectedCamera ? <EditCameraPTZ camera={selectedCamera} /> : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="settings-footer">
                <Button className="clear-show" onClick={handleEdit(selectedCamera)}>{formatMessage({ id: 'app.edit' })}</Button>
                <Button
                  className="clear-data"
                  variant="secondary"
                  onClick={handleDelete(selectedCamera)}
                >{formatMessage({ id: 'app.delete' })}
                </Button>
              </div>
            </div>
          ) : null}
      </div>
    </>
  );
}
